/**
 * Admin Failure Statistics API Client
 *
 * Fetches pre-computed failure statistics from the backend.
 * Statistics are generated by a nightly batch job (compute_failure_stats.py).
 */

import type { ApiResult, ApiError } from './types';

/**
 * Single failure mode statistics
 */
export interface FailureModeData {
  id: string;
  key: string;
  name: string;
  count: number;
  percentage: number;
  trend: string;
  trend_direction: 'up' | 'down' | 'stable';
  lever: string;
  recommendation: string;
}

/**
 * Priority recommendation grouped by architectural lever
 */
export interface PriorityRecommendation {
  rank: number;
  lever: string;
  failure_count: number;
  percentage: number;
  actions: string[];
}

/**
 * Summary statistics
 */
export interface FailureStatsSummary {
  total_feedback: number;
  negative_feedback: number;
  positive_feedback: number;
  classification_rate: number;
}

/**
 * Time period for the statistics
 */
export interface FailureStatsPeriod {
  start: string;
  end: string;
}

/**
 * Complete failure statistics response from the API
 */
export interface FailureStatsResponse {
  id: string;
  report_type: string;
  generated_at: string;
  period: FailureStatsPeriod;
  summary: FailureStatsSummary;
  failure_modes: FailureModeData[];
  priority_recommendations: PriorityRecommendation[];
}

// API base URL - empty string for same-origin, or full URL for cross-origin
const API_BASE_URL = import.meta.env.VITE_API_MANAGEMENT_URL || '';
const API_BASE = `${API_BASE_URL}/api/admin`;

/**
 * Fetch failure statistics from the admin API
 *
 * @param token - Auth0 access token
 * @returns Pre-computed failure statistics or error
 */
export async function getFailureStats(
  token: string
): Promise<ApiResult<FailureStatsResponse>> {
  try {
    const response = await fetch(`${API_BASE}/failure-stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = {
          message: `Request failed with status ${response.status}`,
          code: `HTTP_${response.status}`,
        };
      }
      return { success: false, error };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    const error: ApiError = {
      message: err instanceof Error ? err.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
    return { success: false, error };
  }
}
